import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router";
import {
  Plus,
  Archive,
  Calendar,
  ArchiveRestore,
  Edit,
  FolderOpen,
  CircleUser,
  Search,
} from "lucide-react";
import { toast } from "react-toastify";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { CreateProjectDialog, EditProjectDialog } from "@/components/projects/ProjectDialog";
import { useProjects, useArchiveProject } from "@/hooks/useProjects";
import { Project } from "@/types/project";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import Tooltip from "@/components/shared/Tooltip";
import { GenericErrorDisplay } from "@/components/shared/ErrorDisplay";
import { LoadingDisplay } from "@/components/shared/LoadingDisplay";
import { Main } from "@/components/shared/Main";
import { Header } from "@/components/shared/Header";

export default function ProjectsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showArchived, setShowArchived] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const {
    data: projects,
    isLoading: isProjectsLoading,
    error: isProjectsError,
    mutate: mutateProjects,
  } = useProjects();

  // Read dialog state from URL parameters
  const modalType = searchParams.get("modal");
  const projectId = searchParams.get("projectId");
  const createDialogOpen = modalType === "create";
  const editingProject =
    modalType === "edit" && projectId && projects
      ? projects.find((p) => p.id.toString() === projectId) || null
      : null;

  // Check for invalid project ID and show error toast
  useEffect(() => {
    if (projectId && projects && !editingProject) {
      toast.error(`Project with ID ${projectId} not found`);
      setSearchParams({});
    }
  }, [projectId, projects, editingProject, setSearchParams]);

  const { trigger: archiveProject, isMutating: isArchiving } = useArchiveProject();

  // Dialog handlers
  const openCreateDialog = () => {
    setSearchParams({ modal: "create" });
  };

  const openEditDialog = (project: Project) => {
    setSearchParams({ modal: "edit", projectId: project.id.toString() });
  };

  const closeDialog = () => {
    setSearchParams({});
  };

  const handleArchive = async (project: Project) => {
    try {
      const successMessage = project.isArchived
        ? `Successfully unarchived Project ${project.name}.`
        : `Successfully archived Project ${project.name}!`;
      await archiveProject(
        { id: project.id.toString() },
        {
          onSuccess: () => {
            toast.success(successMessage);
            mutateProjects();
          },
          onError: (err) => {
            toast.error(`Error archiving project: ${err.message}`);
          },
        },
      );
    } catch (error) {
      console.error(error);
      toast.error("Failed to archive project");
    }
  };

  const handleEdit = (project: Project) => {
    openEditDialog(project);
  };

  const formatDate = (date: Date | null) => {
    if (!date) return "-";
    return date.toLocaleDateString();
  };

  if (isProjectsLoading) {
    return <LoadingDisplay />;
  }

  if (isProjectsError) {
    return <GenericErrorDisplay />;
  }

  if (projects.length === 0) {
    return (
      <>
        <div className="grid min-h-full place-items-center px-6 py-24 sm:py-32 lg:px-8">
          <div className="text-center">
            <FolderOpen className="h-24 w-24 mx-auto" />
            <p className="mt-6 text-xl font-medium text-pretty sm:text-xl/8">No Projects</p>
            <p className="text-base font-semibold text-gray-600/50">
              Get started by creating the first Project!
            </p>
            <div className="mt-10 flex items-center justify-center">
              <Button onClick={openCreateDialog}>
                <Plus className="h-4 w-4 mr-2" />
                New Project
              </Button>
            </div>
          </div>
        </div>
        <CreateProjectDialog open={createDialogOpen} onOpenChange={closeDialog} />
      </>
    );
  }

  return (
    <>
      <Header />
      <Main>
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl font-bold">Projects</CardTitle>
            <div className="flex items-center justify-between gap-4 mt-2">
              <div className="flex items-center gap-2 flex-1 max-w-sm">
                <div className="relative w-full">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Filter by name, description, or owner"
                    className="pl-8"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    id="show-archived"
                    checked={showArchived}
                    onCheckedChange={setShowArchived}
                  />
                  <Label htmlFor="show-archived" className="text-sm font-medium">
                    Show Archived
                  </Label>
                </div>
                <Button onClick={openCreateDialog}>
                  <Plus className="w-4 h-4 mr-2" />
                  New Project
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Owner</TableHead>
                  <TableHead>Start Date</TableHead>
                  <TableHead>End Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {projects
                  .filter((project) => (showArchived ? true : !project.isArchived))
                  .filter((project) => {
                    if (!searchQuery) return true;
                    const q = searchQuery.toLowerCase();
                    return (
                      project.name.toLowerCase().includes(q) ||
                      (project.description ?? "").toLowerCase().includes(q) ||
                      project.owner.toLowerCase().includes(q)
                    );
                  })
                  .map((project) => (
                    <TableRow
                      key={project.id}
                      className={`hover:bg-muted/50 cursor-pointer ${project.isArchived ? "opacity-60" : ""}`}
                      onClick={() => navigate(`/projects/${project.id}`)}
                    >
                      <TableCell className="font-medium">
                        {project.name}{" "}
                        {project.isArchived && (
                          <Badge variant="secondary" className="ml-2">
                            Archived
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="max-w-xs truncate">{project.description || "-"}</div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <CircleUser className="w-4 h-4 text-gray-500" />
                          <span className="text-xs text-gray-600 font-mono">{project.owner}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4 text-gray-500" />
                          {formatDate(project.startDate)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4 text-gray-500" />
                          {formatDate(project.endDate)}
                        </div>
                      </TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-2">
                          <Tooltip
                            asChild
                            trigger={
                              <Button variant="ghost" size="sm" onClick={() => handleEdit(project)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                            }
                            content="Edit"
                          />
                          <Tooltip
                            asChild
                            trigger={
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleArchive(project)}
                                disabled={isArchiving}
                              >
                                {project.isArchived ? (
                                  <ArchiveRestore className="h-4 w-4" />
                                ) : (
                                  <Archive className="h-4 w-4" />
                                )}
                              </Button>
                            }
                            content={project.isArchived ? "Unarchive" : "Archive"}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <CreateProjectDialog open={createDialogOpen} onOpenChange={closeDialog} />
        {editingProject && (
          <EditProjectDialog
            open={editingProject !== null}
            onOpenChange={closeDialog}
            project={editingProject}
          />
        )}
      </Main>
    </>
  );
}
